import FInput_PinyinSafeTextCounter from '@/components/FInput_PinyinSafeTextCounter';
import FSelect2 from '@/components/FSelect2';
import FComponentsLib from '@freelog/components-lib';
import { Cascader, DatePicker, Radio } from 'antd';
import img_idCardBack from '@/assets/images/idCardBack.png';
import img_idCardFront from '@/assets/images/idCardFront.png';
import img_businessLicense from '@/assets/images/businessLicense.png';
import styles from './index.less';
import { FI18n, FServiceAPI, FUtil } from '@freelog/tools-lib';
import province_city from '@/assets/data/province_city.json';
import fReadLocalFiles from '@/components/fReadLocalFiles';
import fMessage from '@/components/fMessage';
import fPromiseModalConfirm from '@/components/fPromiseModalConfirm';
import * as AHooks from 'ahooks';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { history } from '@@/core/history';
import useUrlState from '@/hooks/useUrlState';
import FAuthenticationModal from '@/components/FAuthenticationModal';
import FLoading from '@/components/FIcons/FLoading';
import FidPhotoUpload from '@/components/FIdPhotoUpload';
import type { FocusEvent } from 'react';
import * as React from 'react';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;

interface SettlementInfoProps { }

interface SettlementInfoFormData {
  region: string;
  entityType: 'individuals';
  // | 'businesses';
  realName: string;
  realNameError: string;
  idNumber: string;
  idNumberError: string;
  idCardFront: string;
  idCardFrontImage: string;
  idCardBack: string;
  idCardBackImage: string;
  idType: 'short' | 'long';
  idPeriodStart: dayjs.Dayjs | null;
  idPeriodEnd: dayjs.Dayjs | null;
  businessLicensePhoto: string;
  businessName: string;
  registrationAddress: string;
  businessPeriodType: 'timeRange' | 'long';
  businessPeriodStart: dayjs.Dayjs | null;
  businessPeriodEnd: dayjs.Dayjs | null;
  settlementMethod: 'bankCard';
  accountType: 'individuals';
  bankAccount: string;
  bankAccountError: string;
  bankName: string;
  bankRegion: string[] | null;
  bankRegionError: string;
}

const BANK_ACCOUNT_MIN_LENGTH = 8;
const BANK_ACCOUNT_MAX_LENGTH = 32;

const BANK_ACCOUNT_EMPTY_ERROR = '__empty__';

function getBankAccountValidationError(value: string): string {
  const trimmed = value.trim();
  // 为空时只显示红框，不展示错误文案
  if (trimmed === '') {
    return BANK_ACCOUNT_EMPTY_ERROR;
  }
  if (trimmed.length < BANK_ACCOUNT_MIN_LENGTH || trimmed.length > BANK_ACCOUNT_MAX_LENGTH) {
    return '银行账号长度为 8~32 个字符';
  }
  return '';
}

const initVal: SettlementInfoFormData = {
  region: FI18n.i18nNext.t('payout_setting_region_input_option_chinamainland'),
  entityType: 'individuals',
  realName: '',
  realNameError: '',
  idNumber: '',
  idNumberError: '',
  idCardFront: '',
  idCardFrontImage: '',
  idCardBack: '',
  idCardBackImage: '',
  idType: 'short', // short or long
  idPeriodStart: null,
  idPeriodEnd: null,
  businessLicensePhoto: '', // 营业执照证件照片
  businessName: '', // 营业执照主体名称
  registrationAddress: '', // 注册地址
  businessPeriodType: 'timeRange', // 营业期限类型：timeRange or long
  businessPeriodStart: null, // 营业期限日期范围
  businessPeriodEnd: null, // 营业期限日期范围
  settlementMethod: 'bankCard',
  accountType: 'individuals',
  bankAccount: '',
  bankAccountError: '',
  bankName: '',
  bankRegion: null,
  bankRegionError: '',
};

function SettlementInfo({ }: SettlementInfoProps) {
  const [state] = useUrlState<{
    nodeID: string;
  }>();

  const [$step, set$Step, get$Step] = FUtil.Hook.useGetState<number>(1);

  const [$nowData, set$nowData, get$nowData] = FUtil.Hook.useGetState<dayjs.Dayjs>(
    dayjs().startOf('day'),
  );
  // console.log($nowData, 'si8fjl;dkjfl;kjl;k');

  const [$formData, set$FormData, get$FormData] =
    FUtil.Hook.useGetState<SettlementInfoFormData>(initVal);

  const [$accountID, set$AccountID, get$AccountID] = FUtil.Hook.useGetState<string>('');

  const [$showAuthenticationModal, set$showAuthenticationModal, get$showAuthenticationModal] =
    FUtil.Hook.useGetState<boolean>(false);

  async function checkMobileAndProceedAuth() {
    const {
      data: userInfo,
    }: {
      data: {
        mobile: string;
      };
    } = await FServiceAPI.User.currentUserInfo();

    if (userInfo?.mobile) {
      set$showAuthenticationModal(true);
      return;
    }

    const bool = await fPromiseModalConfirm({
      title: '提示',
      description: FI18n.i18nNext.t('payout_setting_alert_no_mobileno_msg'),
      okText: FI18n.i18nNext.t('payout_setting_alert_no_mobileno_btn_ok'),
      cancelText: FI18n.i18nNext.t('payout_setting_alert_no_mobileno_btn_later'),
    });
    if (bool) {
      self.open(
        FUtil.Format.completeUrlByDomain('user') + FUtil.LinkTo.setting() + '?type=security',
      );
    }
  }

  const documentVisibility = AHooks.useDocumentVisibility();

  AHooks.useDebounceEffect(
    () => {
      // console.log(documentVisibility, 'documentVisibility');
      if (documentVisibility === 'visible') {
        handleDocumentVisibility();
      }
    },
    [documentVisibility],
    {
      wait: 100,
    },
  );

  async function handleDocumentVisibility() {
    const nodeID: number = Number(state?.nodeID ?? 0);
    const {
      ret,
      errCode,
      msg,
      data,
    }: {
      ret: number;
      errCode: number;
      msg: string;
      data: {
        accountType: 1;
        availableBalance: number;
        certName: string;
        certNo: string;
        createDate: string;
        currencyType: number;
        freezeBalance: number;
        id: string;
        ownerId: number;
        ownerName: string;
        ownerUserId: number;
        ownerUserName: string;
        status: number;
        totalBalance: number;
        totalExpenditureAmount: number;
        totalIncomeAmount: number;
      };
    } = await FServiceAPI.Payment.queryFinancialAccountInfo({
      accountType: nodeID === 0 ? 1 : 2,
      ownerId: nodeID === 0 ? FUtil.Tool.getUserIDByCookies() : nodeID,
    });
    if (ret !== 0 || errCode !== 0) {
      // fMessage(msg, 'error');
      return;
    }

    if (!data) {
      return;
    }

    if (data.id) {
      history.replace(FUtil.LinkTo.settlementInfo({ accountID: data.id }));
    }
  }

  AHooks.useMount(async () => {
    const nodeID: number = Number(state?.nodeID ?? 0);

    // 已开通结算账户则直接跳转结算信息页，不再弹身份验证弹窗
    const { ret, errCode, data: accountData } = await FServiceAPI.Payment.queryFinancialAccountInfo({
      accountType: nodeID === 0 ? 1 : 2,
      ownerId: nodeID === 0 ? FUtil.Tool.getUserIDByCookies() : nodeID,
    });
    if (ret === 0 && errCode === 0 && accountData?.id) {
      history.replace(FUtil.LinkTo.settlementInfo({ accountID: accountData.id }));
      return;
    }

    await checkMobileAndProceedAuth();
  });

  React.useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible' && !get$showAuthenticationModal()) {
        void checkMobileAndProceedAuth();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  function onChange_bankAccount(value: string) {
    set$FormData({
      ...get$FormData(),
      bankAccount: value,
      bankAccountError: getBankAccountValidationError(value),
    });
  }

  function onChange_bankRegion(value: string[] | null) {
    let error = '';
    if (value === null) {
      error = '请选择开户银行所在地区';
    }
    set$FormData({ ...get$FormData(), bankRegion: value, bankRegionError: error });
  }

  return (
    <>
      <div className={styles.settlementInfoContainer}>
        <div className={styles.headerSection}>
          <h2 className={styles.title}>
            {
              // 结算信息
              FI18n.i18nNext.tAuto('payout_setup_title')
            }
          </h2>
        </div>

        <div className={styles.stepIndicator}>
          <div className={styles.stepItem}>
            <div className={styles.stepCircle}>
              <span className={styles.stepNumber}>1</span>
            </div>
            <span className={styles.stepLabel}>
              {
                // 提交主体信息
                FI18n.i18nNext.tAuto('payout_setup1_title')
              }
            </span>
          </div>

          <div className={styles.stepConnector}></div>

          <div className={styles.stepItem}>
            <div className={`${styles.stepCircle} ${$step === 1 ? styles.stepCircleInactive : ''}`}>
              <span
                className={`${styles.stepNumber} ${$step === 1 ? styles.stepNumberInactive : ''}`}
              >
                2
              </span>
            </div>
            <span className={styles.stepLabel}>

              {
                // 设置结算方式
                FI18n.i18nNext.tAuto('payout_setup2_title')
              }
            </span>
          </div>
        </div>

        {$step === 1 && (
          <>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                {
                  // 所在区域
                  FI18n.i18nNext.tAuto('payout_setting_region_input')
                }
              </div>
              <div className={styles.regionSelector}>
                <div className={styles.selectorWrapper}>
                  <FSelect2
                    value={$formData.region || undefined}
                    onChange={(value) => {
                      // handleInputChange('region', value)
                      set$FormData({
                        ...get$FormData(),
                        region: value,
                      });
                    }}
                    className={styles.selector}
                    // suffixIcon={<DownOutlined className={styles.dropdownIcon} />}
                    options={[{
                      label:
                        // 中国大陆
                        FI18n.i18nNext.tAuto('payout_setting_region_input_option_chinamainland'),
                      value:
                      // '中国大陆'
                      FI18n.i18nNext.t('payout_setting_region_input_option_chinamainland'),
                    }]}
                  />
                </div>
                <div className={styles.hintText}>
                  {
                    // 目前仅针对中国大陆地区的用户开放，其他区域后续开放...
                    FI18n.i18nNext.tAuto('payout_setting_region_input_info')
                  }
                </div>
              </div>
            </div>

            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <div>
                  {
                    // 认证信息
                    FI18n.i18nNext.tAuto('payout_setting_entity_title')
                  }
                </div>
                <div style={{ height: 5 }} />
                <div className={styles.hintText}>
                  {
                    // 个人认证信息需要和银行卡持有人保持一致，身份证信息提交后不可修改，请谨慎提交
                    FI18n.i18nNext.tAuto('payout_setting_entity_info')
                  }
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>
                  {
                    // 主体类型
                    FI18n.i18nNext.tAuto('payout_setting_entity_input_type')
                  }
                </div>
                <div className={styles.selectorWrapper}>
                  <FSelect2
                    value={$formData.entityType || undefined}
                    onChange={(value) => {
                      // handleInputChange('entityType', value);
                      set$FormData({
                        ...get$FormData(),
                        entityType: value,
                      });
                    }}
                    // placeholder={'请选择'}
                    className={styles.selector}
                    options={[
                      {
                        label:
                          // 个人
                          FI18n.i18nNext.tAuto('payout_setting_entity_input_type_option_individual'),
                        value: 'individuals',
                      },
                      // { label: '企业', value: 'businesses' },
                    ]}
                  />
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>
                  {
                    // 身份证照片
                    FI18n.i18nNext.tAuto('payout_setting_entity_input_idphoto')
                  }
                </div>

                <FidPhotoUpload
                  onFrontSuccess={(data) => {
                    const realName = get$FormData().realName
                      ? get$FormData().realName
                      : data.name ?? '';
                    const trimmedName = realName.trim();
                    let realNameError = get$FormData().realNameError;
                    if (trimmedName === '') {
                      // keep existing error (e.g. empty / '#')
                    } else if (trimmedName.length < 2) {
                      realNameError =
                        FI18n.i18nNext.t('payout_setting_entity_input_name_alert_length') ||
                        '姓名必须包含至少两个字符';
                    } else {
                      realNameError = '';
                    }
                    set$FormData({
                      ...get$FormData(),
                      idCardFront: data.certImagePath,
                      realName,
                      realNameError,
                      idNumber: get$FormData().idNumber
                        ? get$FormData().idNumber
                        : data.idNumber ?? '',
                      idNumberError: '',
                    });
                  }}
                  onBackSuccess={(data) => {
                    // console.log(data, '###@#$@#49topjwakesd;fzdj;lk');
                    const validPeriod = data.validPeriod ?? '';
                    const [start, end] = validPeriod.split('-');
                    const idPeriodStart: dayjs.Dayjs = dayjs(start, 'YYYY.MM.DD');
                    const idPeriodEnd: dayjs.Dayjs | null =
                      end === '长期' ? null : dayjs(end, 'YYYY.MM.DD');
                    const idType: 'long' | 'short' = !idPeriodEnd ? 'long' : 'short';
                    set$FormData({
                      ...get$FormData(),
                      idCardBack: data.certImagePath,
                      idPeriodStart: get$FormData().idPeriodStart
                        ? get$FormData().idPeriodStart
                        : idPeriodStart,
                      idPeriodEnd: get$FormData().idPeriodStart
                        ? get$FormData().idPeriodEnd
                        : idPeriodEnd,
                      idType: get$FormData().idPeriodStart ? get$FormData().idType : idType,
                    });
                  }}
                />
                <div className={styles.photoHint}>
                  {
                    // 正面拍摄、清晰、四角完整、无反光或遮挡；不得翻拍、截图、镜像、PS。
                    FI18n.i18nNext.tAuto('payout_setting_entity_input_idphoto_info3')
                  }
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>
                  {
                    // 真实姓名
                    FI18n.i18nNext.tAuto('payout_setting_entity_input_name')
                  }
                </div>
                <div style={{ width: '100%' }}>
                  <FInput_PinyinSafeTextCounter
                    lengthLimit={100}
                    value={$formData.realName}
                    hasError={$formData.realNameError !== ''}
                    onChangeValue={(value) => {
                      const trimmed = value.trim();
                      set$FormData({
                        ...get$FormData(),
                        realName: trimmed,
                        realNameError: '',
                      });
                    }}
                    // placeholder='请输入内容...'
                    style={{ width: '100%' }}
                    onBlur={(e: FocusEvent<HTMLInputElement>) => {
                      const trimmed = e.currentTarget.value.trim();
                      let realNameError = '';
                      if (trimmed === '') {
                        realNameError = '#';
                      } else if (trimmed.length < 2) {
                        realNameError =
                          // 姓名必须包含至少两个字符
                          FI18n.i18nNext.t('payout_setting_entity_input_name_alert_length') ||
                          '姓名必须包含至少两个字符';
                      }
                      set$FormData({
                        ...get$FormData(),
                        realName: trimmed,
                        realNameError,
                      });
                    }}
                  />
                  {$formData.realNameError !== '' && $formData.realNameError !== '#' && (
                    <div className={styles.fieldError}>{$formData.realNameError}</div>
                  )}
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>
                  {
                    // 身份证号
                    FI18n.i18nNext.tAuto('payout_setting_entity_input_idno')
                  }
                </div>
                <div style={{ width: '100%' }}>
                  <FInput_PinyinSafeTextCounter
                    lengthLimit={18}
                    value={$formData.idNumber}
                    hasError={$formData.idNumberError !== ''}
                    onChangeValue={(value) => {
                      set$FormData({
                        ...get$FormData(),
                        idNumber: value,
                        idNumberError: '',
                      });
                    }}
                    // placeholder='请输入内容...'
                    style={{ width: '100%' }}
                    onBlur={() => {
                      let idNumberError: string = '';
                      if (get$FormData().idNumber === '') {
                        idNumberError = '请输入身份证号';
                      }
                      set$FormData({
                        ...get$FormData(),
                        idNumberError: idNumberError,
                      });
                    }}
                  />
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>
                  {
                    // 身份证期限
                    FI18n.i18nNext.tAuto('payout_setting_entity_input_idvalidation')
                  }
                </div>
                <div className={styles.idPeriodContainer}>
                  <div className={styles.radioGroup}>
                    <Radio.Group
                      value={$formData.idType}
                      onChange={(e) => {
                        // console.log(e.target.value, 'e.target.value sedfi8ojkew;lkfjslkdj')
                        set$FormData({
                          ...get$FormData(),
                          idType: e.target.value,
                          idPeriodStart: null,
                          idPeriodEnd: null,
                        });
                      }}
                      className={styles.radioButtons}
                    >
                      <Radio
                        value='short'
                      >
                        {
                          // 短期
                          FI18n.i18nNext.tAuto('payout_setting_entity_input_idvalidation_option')
                        }
                      </Radio>
                      <Radio
                        value='long'
                      >
                        {
                          // 长期
                          FI18n.i18nNext.tAuto('payout_setting_entity_input_idvalidation_option2')
                        }
                      </Radio>
                    </Radio.Group>
                  </div>
                  <div style={{ height: 10 }} />
                  <div className={styles.dateRangeWrapper}>
                    {$formData.idType === 'long' ? (
                      <div className={styles.dateRangeWrapper}>
                        <DatePicker
                          style={{ height: 38 }}
                          placeholder={
                            // 开始时间
                            FI18n.i18nNext.t('payout_setting_entity_input_idvalidation_hint')
                          }
                          value={$formData.idPeriodStart}
                          onChange={(date) => {
                            set$FormData({
                              ...get$FormData(),
                              idPeriodStart: date ?? null,
                            });
                          }}
                          disabledDate={(date) => {
                            return (
                              date.isBefore(dayjs('1900-01-01')) || date.isAfter(get$nowData())
                            );
                          }}
                        />
                      </div>
                    ) : (
                      <RangePicker
                        value={[$formData.idPeriodStart, $formData.idPeriodEnd]}
                        onChange={(dates) => {
                          set$FormData({
                            ...get$FormData(),
                            idPeriodStart: dates?.[0] ?? null,
                            idPeriodEnd: dates?.[1] ?? null,
                          });
                        }}
                        placeholder={[
                          // 开始时间
                          FI18n.i18nNext.t('payout_setting_entity_input_idvalidation_hint'),
                          // 结束时间
                          FI18n.i18nNext.t('payout_setting_entity_input_idvalidation_hint2'),
                        ]}
                        className={styles.dateRange}
                        disabledDate={(date) => {
                          return date.isBefore(dayjs('1900-01-01')) || date.isAfter(get$nowData());
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>

              {$formData.entityType !== 'individuals' && (
                <>
                  {/* 营业执照证件照片 */}
                  <div className={styles.fieldGroup}>
                    <div className={styles.fieldLabel}>营业执照证件照片</div>
                    <div className={styles.businessLicensePhotoContainer}>
                      <div className={styles.photoUpload}>
                        <div className={styles.photoPlaceholder}>
                          <div className={styles.businessLicenseMock}>
                            <img src={img_businessLicense} alt='营业执照照片' />
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={styles.photoHint}>
                      <div>
                        •&nbsp;主体为"个体工商户"时，请填写营业执照上的注册号/统一社会信用代码，格式需满足以下任一条件：
                      </div>
                      <div>•&nbsp;15位数字</div>
                      <div>
                        •&nbsp;18位阿拉伯数字或大写英文字母（不得包含英文字母I/O/Z/S/V），并且以9开头
                      </div>
                      <div>
                        •&nbsp;主体为"企业"时，请填写营业执照上的注册号/统一社会信用代码，格式如下：
                      </div>
                      <div>
                        •&nbsp;18位阿拉伯数字或大写英文字母（不得包含英文字母I/O/Z/S/V），并且以9开头
                      </div>
                    </div>
                  </div>

                  {/* 营业执照主体名称 */}
                  <div className={styles.fieldGroup}>
                    <div className={styles.fieldLabel}>营业执照主体名称</div>
                    <FInput_PinyinSafeTextCounter
                      value={$formData.businessName}
                      onChangeValue={(value) => {
                        // handleInputChange('businessName', value)
                        set$FormData({
                          ...get$FormData(),
                          businessName: value,
                        });
                      }}
                      placeholder='请输入内容...'
                      style={{ width: '100%' }}
                    />
                    <div className={styles.hintText}>
                      个体户证件为以下情况时，按照个体户XXX命名（XXX是营业执照经营人姓名）：营业执照登记名称为空、仅含数字、仅含特殊字符、"无"、"无字号"
                      <br />
                      个体户不能使用"企业""公司"或"农民专业合作社"结尾
                    </div>
                  </div>

                  {/* 注册地址 */}
                  <div className={styles.fieldGroup}>
                    <div className={styles.fieldLabel}>注册地址</div>
                    <FInput_PinyinSafeTextCounter
                      value={$formData.registrationAddress}
                      onChangeValue={(value) => {
                        // handleInputChange('registrationAddress', value)
                        set$FormData({
                          ...get$FormData(),
                          registrationAddress: value,
                        });
                      }}
                      placeholder='请输入内容...'
                      style={{ width: '100%' }}
                    />
                    <div className={styles.hintText}>
                      主体为"企业/个体户"时填写营业执照的注册地址，若该字段未填写，系统将会查询国家工商信息填入。需注意若工商信息查询不到，则会被审核驳回。
                    </div>
                  </div>

                  {/* 营业期限 */}
                  <div className={styles.fieldGroup}>
                    <div className={styles.fieldLabel}>营业期限</div>
                    <div className={styles.businessPeriodContainer}>
                      <div className={styles.radioGroup}>
                        <Radio.Group
                          value={$formData.businessPeriodType}
                          onChange={(e) => {
                            // handleInputChange('businessPeriodType', e.target.value)
                            set$FormData({
                              ...get$FormData(),
                              businessPeriodType: e.target.value,
                            });
                          }}
                          className={styles.radioButtons}
                        >
                          <Radio
                            value='timeRange'
                          // className={styles.radioItem}
                          >
                            时间范围
                          </Radio>
                          <Radio
                            value='long'
                          // className={styles.radioItem}
                          >
                            长期
                          </Radio>
                        </Radio.Group>
                      </div>
                      <div style={{ height: 10 }} />
                      <div className={styles.dateRangeWrapper}>
                        <RangePicker
                          value={[$formData.businessPeriodStart, $formData.businessPeriodEnd]}
                          onChange={(dates) => {
                            // handleInputChange('businessPeriod', dates)
                            set$FormData({
                              ...get$FormData(),
                              businessPeriodStart: dates?.[0] ?? null,
                              businessPeriodEnd: dates?.[1] ?? null,
                            });
                          }}
                          placeholder={['开始时间', '结束时间']}
                          className={styles.dateRange}
                          disabled={$formData.businessPeriodType === 'long'}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className={styles.submitSection}>
              <FComponentsLib.FRectBtn
                disabled={
                  !$formData.idCardFront ||
                  !$formData.idCardBack ||
                  !$formData.idPeriodStart ||
                  ($formData.idType === 'short' && !$formData.idPeriodEnd) ||
                  !$formData.realName ||
                  !$formData.idNumber ||
                  $formData.realNameError !== '' ||
                  $formData.idNumberError !== ''
                }
                type='primary'
                onClick={async () => {
                  const nodeID: number = Number(state?.nodeID ?? 0);

                  const {
                    data: userInfo,
                  }: {
                    data: {
                      mobile: string;
                      userId: number;
                    };
                  } = await FServiceAPI.User.currentUserInfo();

                  const {
                    idNumber,
                    realName,
                    idCardFront,
                    idCardBack,
                    idPeriodStart,
                    idPeriodEnd,
                    idType,
                  } = get$FormData();
                  const params: Parameters<typeof FServiceAPI.Payment.openFinancialAccount>[0] = {
                    accountType: nodeID ? 2 : 1,
                    ownerId: nodeID ? nodeID : userInfo?.userId,
                    certType: '00',
                    certNo: idNumber.trim(),
                    name: realName.trim(),
                    certValidityType: idType === 'long' ? 1 : 0,
                    certBeginDate: idPeriodStart?.format(FUtil.Predefined.momentDateFormat) ?? '',
                    certEndDate: idPeriodEnd?.format(FUtil.Predefined.momentDateFormat) || undefined,
                    mobile: userInfo?.mobile ?? '',
                    certImagePaths: [idCardFront, idCardBack],
                  };
                  const {
                    ret,
                    errCode,
                    msg,
                    data,
                  }: { ret: number; errCode: number; msg: string; data: { id: string } } =
                    await FServiceAPI.Payment.openFinancialAccount(params);
                  if (ret !== 0 || errCode !== 0) {
                    fMessage(msg, 'error');
                    return;
                  }

                  set$AccountID(data.id);

                  set$Step(2);
                }}
              >
                {
                  // 提交
                  FI18n.i18nNext.tAuto('payout_setting_entity_btn_submit')
                }
              </FComponentsLib.FRectBtn>
            </div>
          </>
        )}

        {$step === 2 && (
          <>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                <div>
                  {
                    // 结算账号
                    FI18n.i18nNext.tAuto('payout_setting_payoutmethod_title')
                  }
                </div>
                <div style={{ height: 5 }} />
                <div className={styles.hintText}>
                  {
                    FI18n.i18nNext.tAuto('payout_setting_payoutmethod_info')
                  }
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>
                  {
                    // 结算方式
                    FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input')
                  }
                </div>
                <div className={styles.selectorWrapper}>
                  <FSelect2
                    value={$formData.settlementMethod || undefined}
                    onChange={(value) => {
                      // handleInputChange('settlementMethod', value)
                      set$FormData({
                        ...get$FormData(),
                        settlementMethod: value,
                      });
                    }}
                    className={styles.selector}
                    // suffixIcon={<DownOutlined className={styles.dropdownIcon} />}
                    options={[{
                      label:
                        // 银行卡
                        FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_option_debitcard'),
                      value: 'bankCard',
                    }]}
                    placeholder={'请选择'}
                  />
                </div>
                <div className={styles.hintText}>
                  {
                    // 目前仅提供银行卡的结算方式，其他方式后续开放...
                    FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_info')
                  }
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>
                  {
                    // 账户类型
                    FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_accounttype')
                  }
                </div>
                <div className={styles.selectorWrapper}>
                  <FSelect2
                    value={$formData.accountType || undefined}
                    onChange={(value) => {
                      set$FormData({
                        ...get$FormData(),
                        accountType: value,
                      });
                    }}
                    // placeholder='请选择'
                    className={styles.selector}
                    options={[
                      {
                        label:
                          // 个人账户
                          FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_accounttype_option_individual'),
                        value: 'individuals',
                      },
                      // { label: '企业账户', value: '企业账户' },
                    ]}
                  />
                </div>
              </div>

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>
                  {
                    // 银行账号
                    FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_bankaccount')
                  }
                </div>
                <div style={{ width: '100%' }}>
                  <FInput_PinyinSafeTextCounter
                    lengthLimit={BANK_ACCOUNT_MAX_LENGTH}
                    value={$formData.bankAccount}
                    hasError={$formData.bankAccountError !== ''}
                    onChangeValue={(value) => {
                      onChange_bankAccount(value.replace(/\s/g, ''));
                    }}
                    // placeholder='请输银行账号'
                    style={{ width: '100%' }}
                    onBlur={() => {
                      onChange_bankAccount(get$FormData().bankAccount.trim());
                    }}
                  />
                  {$formData.bankAccountError !== '' &&
                    $formData.bankAccountError !== BANK_ACCOUNT_EMPTY_ERROR && (
                      <div className={styles.fieldError}>{$formData.bankAccountError}</div>
                    )}
                </div>
              </div>

              {/* <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>开户银行 payout_setting_payoutmethod_input_bankname</div>
                <div className={styles.selectorWrapper}>
                  <AutoComplete
                    value={$formData.bankName || undefined}
                    onChange={(value) => {
                      // handleInputChange('bankName', value)
                      set$FormData({
                        ...get$FormData(),
                        bankName: value,
                      });
                    }}
                    placeholder={'请选择'}
                    className={styles.selector}
                    // suffixIcon={<DownOutlined className={styles.dropdownIcon} />}
                    options={bank_names.map((name) => ({ label: name, value: name }))}
                    showSearch
                    filterOption={(input, option) => {
                      return option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false;
                    }}
                  />
                </div>
              </div> */}

              <div className={styles.fieldGroup}>
                <div className={styles.fieldLabel}>
                  {
                    // 开户银行所在地区
                    FI18n.i18nNext.tAuto('payout_setting_payoutmethod_input_bankregion')
                  }
                </div>
                <div className={styles.selectorWrapper}>
                  <Cascader
                    value={$formData.bankRegion ?? undefined}
                    onChange={(value) => {
                      onChange_bankRegion(value as string[]);
                    }}
                    // placeholder={'请选择'}
                    className={styles.selector}
                    options={province_city}
                    onBlur={() => {
                      onChange_bankRegion(get$FormData().bankRegion);
                    }}
                    status={$formData.bankRegionError ? 'error' : undefined}
                  />
                </div>
              </div>
            </div>

            <div className={styles.submitSection}>
              <FComponentsLib.FRectBtn
                disabled={
                  $formData.bankRegion === null ||
                  $formData.bankRegionError !== '' ||
                  $formData.bankAccountError !== '' ||
                  $formData.bankAccount === ''
                }
                type='primary'
                onClick={async () => {
                  const { bankName, bankRegion, bankAccount, realName } = get$FormData();

                  const params2: Parameters<typeof FServiceAPI.Payment.bindWithdrawCard>[0] = {
                    accountId: get$AccountID(),
                    cardType: 1,
                    // bankName: bankName,
                    // cardName: realName,
                    cardNo: bankAccount,
                    provId: bankRegion?.[0] ?? '',
                    areaId: bankRegion?.[1] ?? '',
                  };
                  const {
                    ret: ret2,
                    errCode: errCode2,
                    msg: msg2,
                    data: data2,
                  } = await FServiceAPI.Payment.bindWithdrawCard(params2);
                  if (ret2 !== 0 || errCode2 !== 0) {
                    fMessage(msg2, 'error');
                    return;
                  }

                  history.push(
                    FUtil.LinkTo.settlementInfo({
                      accountID: get$AccountID(),
                    }),
                  );
                }}
              >
                {
                  // 提交
                  FI18n.i18nNext.tAuto('payout_setting_payoutmethod_btn_submit')
                }
              </FComponentsLib.FRectBtn>
            </div>
          </>
        )}
      </div>

      <FAuthenticationModal
        open={$showAuthenticationModal}
        onOk={() => {
          set$showAuthenticationModal(false);
        }}
        onCancel={() => {
          set$showAuthenticationModal(false);
          window.close();
          history.back();
        }}
      />
    </>
  );
}

export default SettlementInfo;

// function fileToImageUrl(file: File) {
//   return URL.createObjectURL(file);
// }
